// src/components/Sidebar.js
import React, { useContext } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Add as AddIcon,
  List as ListIcon,
  ExitToApp as ExitToAppIcon,
  LocalShipping as DispatchIcon,
  Receipt as InvoiceIcon,
  Cancel as CancelIcon,
  DeliveryDining as DeliveryDiningIcon, // Add DeliveryDining icon for Livreurs
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';

const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);
  const { isCollapsed } = useSidebar();

  const sidebarWidth = isCollapsed ? 80 : 260;

  // Check if user has permission to access Dispatch, Facturation, Cancel Commande CB, and Livreurs
  const hasRestrictedAccess = user && ['admin', 'manager'].includes(user.role);

  // Define navigation items
  const navItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    ...(hasRestrictedAccess
      ? [{ text: 'Create Restaurant', icon: <AddIcon />, path: '/create-restaurant' }]
      : []),
    { text: 'Restaurants', icon: <ListIcon />, path: '/restaurants' },
    {
      text: 'Dispatch',
      icon: <DispatchIcon />,
      path: '/dispatch',
      disabled: !hasRestrictedAccess,
    },
    {
      text: 'Facturation',
      icon: <InvoiceIcon />,
      path: '/invoices',
      disabled: !hasRestrictedAccess,
    },
    {
      text: 'Cancel Commande CB',
      icon: <CancelIcon />,
      path: '/cancel-commande-cb',
      disabled: !hasRestrictedAccess,
    },
    {
      text: 'Livreurs',
      icon: <DeliveryDiningIcon />,
      path: '/livreurs',
      disabled: !hasRestrictedAccess,
    },
  ];

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: sidebarWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: sidebarWidth,
          boxSizing: 'border-box',
          background: 'linear-gradient(180deg, #E6F7F5 0%, #D1EFEC 100%)',
          borderRight: 'none',
          boxShadow: '2px 0 15px rgba(0, 0, 0, 0.05)',
          transition: 'width 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          p: isCollapsed ? 0.5 : 2,
          minHeight: isCollapsed ? 56 : 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {!isCollapsed && (
          <Typography
            variant="h5"
            sx={{
              color: '#21ccbb',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
            }}
          >
            Teal
          </Typography>
        )}
      </Box>

      {/* User Profile Section */}
      {!isCollapsed && (
        <Box
          sx={{
            p: 2,
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mt: 1,
              fontSize: '1.1rem',
              fontWeight: 600,
              color: '#3A3A3A',
              letterSpacing: '0.5px',
            }}
          >
            {user ? `${user.prenom} ${user.nom}` : 'Guest'}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              color: '#A3A3A3',
              fontSize: '0.85rem',
              fontWeight: 400,
              mt: 0.5,
            }}
          >
            {user ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Project Manager'}
          </Typography>
        </Box>
      )}

      <Divider sx={{ borderColor: '#E0E0E0', mx: isCollapsed ? 1 : 2, flexShrink: 0 }} />

      {/* Navigation Items */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          pt: isCollapsed ? 0.5 : 1,
        }}
      >
        <List sx={{ pb: 2 }}>
          {navItems.map((item) => (
            <Tooltip
              key={item.text}
              title={item.disabled ? 'Available for Admins and Managers only' : item.text}
              disableHoverListener={!item.disabled && !isCollapsed}
            >
              <ListItem
                button
                component={item.disabled ? 'div' : Link}
                to={item.disabled ? undefined : item.path}
                disabled={item.disabled}
                sx={{
                  py: 1,
                  mx: isCollapsed ? 1 : 2,
                  borderRadius: '10px',
                  mb: 0.5,
                  transition: 'all 0.3s ease',
                  bgcolor: location.pathname === item.path && !item.disabled ? '#21ccbb' : 'transparent',
                  color: location.pathname === item.path && !item.disabled ? '#fff' : '#3A3A3A',
                  '&:hover': {
                    bgcolor:
                      item.disabled ? 'transparent' : location.pathname === item.path ? '#1ab7a8' : '#E6F7F5',
                    transform: item.disabled ? 'none' : 'translateX(5px)',
                    boxShadow: item.disabled ? 'none' : '0 2px 10px rgba(0, 0, 0, 0.05)',
                  },
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  opacity: item.disabled ? 0.5 : 1,
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path && !item.disabled ? '#fff' : '#21ccbb',
                    minWidth: isCollapsed ? 'auto' : '40px',
                    transition: 'color 0.3s ease',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: '0.95rem',
                      letterSpacing: '0.3px',
                    }}
                  />
                )}
              </ListItem>
            </Tooltip>
          ))}
          <ListItem
            button
            onClick={logout}
            sx={{
              py: 1,
              mx: isCollapsed ? 1 : 2,
              borderRadius: '10px',
              mb: 0.5,
              transition: 'all 0.3s ease',
              bgcolor: 'transparent',
              cursor: 'pointer',
              color: '#d32f2f', // Red text color
              '&:hover': {
                bgcolor: '#ffebee', // Light red background on hover
                transform: 'translateX(5px)',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
              },
              justifyContent: isCollapsed ? 'center' : 'flex-start',
            }}
          >
            <ListItemIcon
              sx={{
                cursor: 'pointer',
                color: '#d32f2f', // Red icon color
                minWidth: isCollapsed ? 'auto' : '40px',
                transition: 'color 0.3s ease',
                '&:hover': {
                  color: '#b71c1c', // Darker red on hover
                },
              }}
            >
              <Tooltip title="Logout" disableHoverListener={!isCollapsed}>
                <ExitToAppIcon />
              </Tooltip>
            </ListItemIcon>
            {!isCollapsed && (
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  letterSpacing: '0.3px',
                  cursor: 'pointer',
                }}
              />
            )}
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;